<template>

  <div class="d-flex flex-column page-container">
    <v-sheet class="ma-1">
      <div class="d-flex flex-row flex-wrap align-center ga-2 mb-1">
        <v-select
          v-model="filterCountry"
          :items="availableCountries"
          label="Country"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 180px; max-width: 260px;"
          aria-label="Filter by Country"
        />
        <v-select
          v-model="filterRegion"
          :items="availableRegions"
          label="Region"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 180px; max-width: 260px;"
          aria-label="Filter by Region"
        />
        <v-select
          v-model="filterStatus"
          :items="statusOptions"
          item-title="title"
          item-value="value"
          label="Status"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 180px; max-width: 260px;"
          aria-label="Filter by Status"
        />
        <v-select
          v-model="filterDataFilter"
          :items="dataFilterOptions"
          item-title="title"
          item-value="value"
          label="Data Filter"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 200px; max-width: 310px;"
          aria-label="Filter by Data Filter"
        />
      </div>
      <div class="d-flex flex-row align-center">
        <v-text-field v-model="searchBy" label="Search by Affinity Id | Company Name"
          prepend-inner-icon="mdi-magnify" variant="outlined" style="min-width: 400px;" aria-label="Search by Affinity ID or Company Name" />
        <v-btn class="ml-2" @click="getDistributorRecords" icon="mdi-magnify" size="x-large" variant="text" aria-label="Search distributors" />
        <v-btn
          v-if="systemStore.userDetail.isOrganizationAdmin"
          class="ml-2"
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          @click="openAdd"
          @keydown.enter="openAdd"
          aria-label="Add distributor"
          tabindex="0">
          Add Distributor
        </v-btn>
        <div style="width: 50%;"></div>
      </div>
    </v-sheet>
    <v-sheet class="ma-1">
      <v-card
        variant="outlined"
        title="Manage Distributor"
        flat>
        <template v-slot:text>
          <v-text-field v-model="search" label="Filter" prepend-inner-icon="mdi-filter-outline"
            variant="outlined" single-line aria-label="Filter table"></v-text-field>
        </template>

        <div class="table-scroll-wrapper">
          <v-data-table
            :items-per-page="50"
            :items-per-page-options="[5, 10, 25, 50, -1]"
            :headers="headers"
            :items="filteredRecords"
            :search="search"
            item-key="id"
            fixed-header
            role="table"
            aria-label="Manage distributor data"
            class="table-fixed">

            <template v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="resizable-th"
                  :style="{ width: widthFor(column.key), minWidth: widthFor(column.key) }"
                >
                  <span
                    class="th-label"
                    :class="{ sortable: column.sortable }"
                    role="button"
                    :tabindex="column.sortable ? 0 : -1"
                    :aria-label="column.sortable ? `Sort by ${column.title}` : column.title"
                    @click="column.sortable && toggleSort(column)"
                    @keydown.enter="column.sortable && toggleSort(column)"
                    @keydown.space.prevent="column.sortable && toggleSort(column)"
                  >
                    {{ column.title }}
                    <v-icon
                      v-if="column.sortable && isSorted(column)"
                      :icon="getSortIcon(column)"
                      size="small"
                    />
                  </span>
                  <span
                    v-if="column.key !== 'edit'"
                    class="col-resize-handle"
                    aria-hidden="true"
                    title="Drag to resize column"
                    @pointerdown="startResize($event, column.key)"
                  />
                </th>
              </tr>
            </template>

            <template v-slot:item.statusLink="{ item }">
              {{ getStatusTitle(item.statusLink) }}
            </template>

            <template v-slot:item.dataFilterLink="{ item }">
              {{ getDataFilterTitle(item.dataFilterLink) }}
            </template>

            <template v-slot:item.edit="{ item }">
              <v-btn icon="mdi-pencil" @click="edit(item)" variant="text" aria-label="Edit distributor" />
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-sheet>
  </div>

  <div>
    <Transition name="modal">
      <div v-if="editShow" class="modal-mask">
        <div class="modal-wrapper">
          <div class="modal-container">
            <div class="modal-header">
              <h3 class="modal-title">{{ editTitle }}</h3>
              <div class="modal-actions">
                <v-btn type="button" icon="mdi-history" class="modal-save-button" size="x-small" variant="text" @click.stop="openAuditLog" aria-label="View audit log">
                  <v-icon>mdi-history</v-icon>
                  <v-tooltip activator="parent" location="top">View Audit Log</v-tooltip>
                </v-btn>
                <v-btn v-if="systemStore.userDetail.isOrganizationAdmin" icon="mdi-check-bold" type="submit" form="edit-distributor-form" class="modal-save-button" size="x-small" variant="text" aria-label="Save distributor">
                  <v-icon>mdi-check-bold</v-icon>
                  <v-tooltip activator="parent" location="top">Save and Close</v-tooltip>
                </v-btn>
                <v-btn icon="mdi-close-thick" class="modal-cancel-button" @click="close" size="x-small" variant="text" aria-label="Cancel edit">
                  <v-icon>mdi-close-thick</v-icon>
                  <v-tooltip activator="parent" location="top">Cancel and Close</v-tooltip>
                </v-btn>
              </div>
            </div>
            <div class="modal-body">
              <v-form id="edit-distributor-form" @submit.prevent="saveDistributor">
                <v-text-field label="Affinity Id" v-model="editDistributor.affinityID" density="comfortable" readonly></v-text-field>
                <v-text-field label="Company" v-model="editDistributor.companyName" density="comfortable" readonly></v-text-field>
                <v-text-field label="Country" v-model="editDistributor.country" density="comfortable" readonly></v-text-field>
                <v-text-field label="Sub Region" v-model="editDistributor.region" density="comfortable" readonly></v-text-field>
                <v-text-field label="Email" v-model="editDistributor.email" density="comfortable" variant="outlined"></v-text-field>
                <v-text-field label="Straight to C/N" v-model="editDistributor.straightToCN" density="comfortable" readonly></v-text-field>
                <v-text-field label="Cross Border" v-model="editDistributor.crossBorder" density="comfortable" readonly></v-text-field>
                <v-select
                  v-model="editDistributor.statusLink"
                  :items="statusOptions"
                  item-title="title"
                  item-value="value"
                  label="Status"
                  variant="outlined"
                  density="comfortable"
                  clearable>
                </v-select>
                <v-select
                  v-model="editDistributor.dataFilterLink"
                  :items="dataFilterOptions"
                  item-title="title"
                  item-value="value"
                  label="Data Filter"
                  variant="outlined"
                  density="comfortable">
                </v-select>
              </v-form>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>

  <DistributorAuditLog
    v-model="auditLogShow"
    :distributor-id="auditLogDistributorId"
    :distributor-label="auditLogLabel"
  />

  <div>
    <Transition name="modal">
      <div v-if="addShow" class="modal-mask">
        <div class="modal-wrapper">
          <div class="modal-container">
            <div class="modal-header">
              <h3 class="modal-title">Add Distributor</h3>
              <div class="modal-actions">
                <v-btn
                  v-if="systemStore.userDetail.isOrganizationAdmin"
                  icon="mdi-check-bold"
                  type="submit"
                  form="add-distributor-form"
                  class="modal-save-button"
                  size="x-small"
                  variant="text"
                  :disabled="!!affinityError || affinityLookupPending"
                  aria-label="Save new distributor">
                  <v-icon>mdi-check-bold</v-icon>
                  <v-tooltip activator="parent" location="top">Save and Close</v-tooltip>
                </v-btn>
                <v-btn icon="mdi-close-thick" class="modal-cancel-button" @click="closeAdd" size="x-small" variant="text" aria-label="Cancel add distributor">
                  <v-icon>mdi-close-thick</v-icon>
                  <v-tooltip activator="parent" location="top">Cancel and Close</v-tooltip>
                </v-btn>
              </div>
            </div>
            <div class="modal-body">
              <v-form id="add-distributor-form" @submit.prevent="saveNewDistributor">
                <v-text-field
                  label="Affinity Id"
                  v-model="addDistributor.affinityID"
                  density="comfortable"
                  variant="outlined"
                  :error-messages="affinityError"
                  :loading="affinityLookupPending"
                  @update:model-value="onAffinityIdInput"
                  @blur="lookupAffinityId"
                  aria-label="Affinity Id"
                  aria-describedby="affinity-id-error">
                </v-text-field>
                <v-text-field label="Company" v-model="addDistributor.companyName" density="comfortable" variant="outlined" aria-label="Company"></v-text-field>
                <v-select
                  v-model="addDistributor.country"
                  :items="countryOptions"
                  item-title="country"
                  item-value="country"
                  label="Country"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  aria-label="Country"
                  @update:model-value="onCountrySelected">
                </v-select>
                <v-text-field label="Sub Region" v-model="addDistributor.region" density="comfortable" variant="outlined" aria-label="Sub Region"></v-text-field>
                <v-text-field label="Email" v-model="addDistributor.email" density="comfortable" variant="outlined" aria-label="Email"></v-text-field>
                <v-text-field label="Straight to C/N" v-model="addDistributor.straightToCN" density="comfortable" variant="outlined" aria-label="Straight to credit note"></v-text-field>
                <v-text-field label="Cross Border" v-model="addDistributor.crossBorder" density="comfortable" variant="outlined" aria-label="Cross Border"></v-text-field>
                <v-select
                  v-model="addDistributor.statusLink"
                  :items="statusOptions"
                  item-title="title"
                  item-value="value"
                  label="Status"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  aria-label="Status">
                </v-select>
                <v-select
                  v-model="addDistributor.dataFilterLink"
                  :items="dataFilterOptions"
                  item-title="title"
                  item-value="value"
                  label="Data Filter"
                  variant="outlined"
                  density="comfortable"
                  aria-label="Data Filter">
                </v-select>
              </v-form>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>

</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useDistributorStore } from '@/stores/distributor'
import { useSystemStore } from '@/stores/system'
import { useCountryStore } from '@/stores/country'
import DistributorAuditLog from '@/components/DistributorAuditLog.vue'

const distributorStore = useDistributorStore()
const systemStore = useSystemStore()
const countryStore = useCountryStore()

// Vuetify data table has no built-in column resizing — custom header row + drag grip per column.
const columnWidths = ref({})

const widthFor = (key) => {
  if (columnWidths.value[key]) {
    return `${columnWidths.value[key]}px`
  }
  const header = headers.find(h => h.key === key)
  return header?.width || header?.minWidth
}

const startResize = (e, key) => {
  e.preventDefault()
  e.stopPropagation()

  const th = e.target.closest('th')
  const startX = e.clientX
  const startWidth = th.offsetWidth
  const handle = e.target

  handle.setPointerCapture(e.pointerId)
  document.body.style.userSelect = 'none'

  const onMove = (ev) => {
    columnWidths.value[key] = Math.max(60, startWidth + (ev.clientX - startX))
  }

  const onUp = () => {
    handle.releasePointerCapture(e.pointerId)
    handle.removeEventListener('pointermove', onMove)
    handle.removeEventListener('pointerup', onUp)
    document.body.style.userSelect = ''
  }

  handle.addEventListener('pointermove', onMove)
  handle.addEventListener('pointerup', onUp)
}

const distributorRecords = ref([])
const countryOptions = ref([])
const searchBy = ref('')
const search = ref('')

const filterCountry = ref([])
const filterRegion = ref([])
const filterStatus = ref([])
const filterDataFilter = ref([])

const availableCountries = computed(() => {
  const countries = distributorRecords.value.map(r => r.country).filter(c => c && c !== '-')
  return [...new Set(countries)].sort()
})

const availableRegions = computed(() => {
  const regions = distributorRecords.value.map(r => r.region).filter(r => r && r !== '-')
  return [...new Set(regions)].sort()
})

const filteredRecords = computed(() => {
  return distributorRecords.value.filter(r => {
    if (filterCountry.value.length && !filterCountry.value.includes(r.country)) return false
    if (filterRegion.value.length && !filterRegion.value.includes(r.region)) return false
    if (filterStatus.value.length && !filterStatus.value.includes(r.statusLink)) return false
    if (filterDataFilter.value.length && !filterDataFilter.value.includes(r.dataFilterLink)) return false
    return true
  })
})

const editShow = ref(false)
const editTitle = ref('Edit Distributor')
const editDistributor = ref({})

const addShow = ref(false)
const addDistributor = ref(emptyAddDistributor())
const affinityError = ref('')
const affinityLookupPending = ref(false)
const lastLookedUpAffinityId = ref('')

const auditLogShow = ref(false)
const auditLogDistributorId = ref(null)
const auditLogLabel = ref('')

function emptyAddDistributor() {
  return {
    affinityID: '',
    companyName: '',
    country: '',
    countryCode: '',
    region: '',
    email: '',
    straightToCN: '',
    crossBorder: '',
    statusLink: 1,
    dataFilterLink: 0
  }
}

const statusOptions = [
  { value: 1, title: 'Active' },
  { value: 2, title: 'Cancelled' },
  { value: 3, title: 'Deleted' },
  { value: 4, title: 'Disabled' },
  { value: 5, title: 'Expired' },
  { value: 6, title: 'Pending' }
]

// Matches existing backend usage of DataFilterLink values (0, 9, 10, 11)
const dataFilterOptions = [
  { value: 0, title: 'None' },
  { value: 9, title: 'Find My Disti' },
  { value: 10, title: 'Redemption' },
  { value: 11, title: 'Find My Disti and Redemption' }
]

const headers = [
  { key: 'affinityID', title: 'Affinity Id', width: '130px', minWidth: '130px', sortable: true },
  { key: 'companyName', title: 'Company Name', width: '260px', minWidth: '260px', sortable: true },
  { key: 'country', title: 'Country', width: '140px', minWidth: '140px', sortable: true },
  { key: 'region', title: 'Region', width: '160px', minWidth: '160px', sortable: true },
  { key: 'email', title: 'Email', width: '280px', minWidth: '280px', sortable: true },
  { key: 'noOfRegisteredUsers', title: 'Registered Users', width: '160px', minWidth: '160px', sortable: true },
  { key: 'statusLink', title: 'Status', width: '120px', minWidth: '120px', sortable: true },
  { key: 'dataFilterLink', title: 'Data Filter', width: '300px', minWidth: '300px', sortable: true },
  { key: 'edit', title: 'Edit', width: '80px', minWidth: '80px', sortable: false }
]

onMounted(() => {
  getDistributorRecords()
  countryStore.getEMEACountries().then(result => {
    if (result.success) {
      countryOptions.value = result.countries || []
    }
  })
})

const getStatusTitle = (value) => {
  const found = statusOptions.find(o => o.value === value)
  return found ? found.title : value
}

const getDataFilterTitle = (value) => {
  const found = dataFilterOptions.find(o => o.value === value)
  return found ? found.title : value
}

const getDistributorRecords = () => {
  distributorStore.getDistributorRecordsForAdmin(searchBy.value).then(result => {
    if (result.hasData) {
      distributorRecords.value = result.distributorRecords
    } else {
      distributorRecords.value = []
    }
  })
}

const edit = (item) => {
  editTitle.value = 'Edit Distributor'
  editDistributor.value = { ...item }
  editShow.value = true
}

const saveDistributor = () => {
  distributorStore.updateDistributorStatusAndDataFilter({
    id: editDistributor.value.id,
    statusLink: editDistributor.value.statusLink != null && editDistributor.value.statusLink !== '' ? editDistributor.value.statusLink : null,
    dataFilterLink: editDistributor.value.dataFilterLink != null && editDistributor.value.dataFilterLink !== '' ? editDistributor.value.dataFilterLink : null,
    email: editDistributor.value.email ?? null
  }).then(result => {
    if (result.success === true) {
      close()
    }
  })
}

const close = () => {
  editShow.value = false
  getDistributorRecords()
}

const openAuditLog = () => {
  auditLogDistributorId.value = editDistributor.value.id
  auditLogLabel.value = `${editDistributor.value.companyName} (${editDistributor.value.country})`
  auditLogShow.value = true
}

const openAdd = () => {
  addDistributor.value = emptyAddDistributor()
  affinityError.value = ''
  lastLookedUpAffinityId.value = ''
  addShow.value = true
}

const onAffinityIdInput = () => {
  const current = String(addDistributor.value.affinityID ?? '').trim()
  if (current !== lastLookedUpAffinityId.value) {
    affinityError.value = ''
    lastLookedUpAffinityId.value = ''
  }
}

const onCountrySelected = (countryName) => {
  const selected = countryOptions.value.find(c => c.country === countryName)
  if (selected) {
    addDistributor.value.country = selected.country
    addDistributor.value.countryCode = selected.iso || ''
    if (selected.subRegion) {
      addDistributor.value.region = selected.subRegion
    }
  } else {
    addDistributor.value.countryCode = ''
  }
}

const closeAdd = () => {
  addShow.value = false
  affinityError.value = ''
  affinityLookupPending.value = false
  getDistributorRecords()
}

const lookupAffinityId = () => {
  const affinityId = String(addDistributor.value.affinityID ?? '').trim()
  if (!affinityId) {
    affinityError.value = ''
    lastLookedUpAffinityId.value = ''
    return
  }
  if (!/^\d+$/.test(affinityId)) {
    affinityError.value = 'Affinity Id must be numeric'
    return
  }
  if (affinityId === lastLookedUpAffinityId.value) {
    return
  }

  affinityLookupPending.value = true
  affinityError.value = ''
  distributorStore.lookupAffinityForAddDistributor(affinityId).then(result => {
    affinityLookupPending.value = false
    lastLookedUpAffinityId.value = affinityId

    if (result.existsInDistributor || result.success === false) {
      affinityError.value = (result.messages && result.messages[0]) || 'Affinity Id already exists in distributor table'
      return
    }

    affinityError.value = ''
  }).catch(() => {
    affinityLookupPending.value = false
    affinityError.value = 'Unable to look up Affinity Id'
  })
}

const saveNewDistributor = () => {
  const affinityId = String(addDistributor.value.affinityID ?? '').trim()
  if (!affinityId || !/^\d+$/.test(affinityId)) {
    affinityError.value = 'Affinity Id is required and must be numeric'
    return
  }
  if (affinityError.value) {
    return
  }
  if (!addDistributor.value.companyName || !String(addDistributor.value.companyName).trim()) {
    systemStore.showMessage('Company Name is required', { color: 'error' })
    return
  }

  const runCreate = () => {
    distributorStore.createDistributor({
      affinityId: Number(affinityId),
      companyName: addDistributor.value.companyName,
      country: addDistributor.value.country || null,
      countryCode: addDistributor.value.countryCode || null,
      region: addDistributor.value.region || null,
      email: addDistributor.value.email || null,
      straightToCN: addDistributor.value.straightToCN || null,
      crossBorder: addDistributor.value.crossBorder || null,
      statusLink: addDistributor.value.statusLink != null && addDistributor.value.statusLink !== '' ? addDistributor.value.statusLink : null,
      dataFilterLink: addDistributor.value.dataFilterLink != null && addDistributor.value.dataFilterLink !== '' ? addDistributor.value.dataFilterLink : 0
    }).then(result => {
      if (result.success === true) {
        closeAdd()
      } else if (result.existsInDistributor || (result.messages && result.messages.length)) {
        affinityError.value = (result.messages && result.messages[0]) || affinityError.value
      }
    })
  }

  if (affinityId !== lastLookedUpAffinityId.value) {
    affinityLookupPending.value = true
    distributorStore.lookupAffinityForAddDistributor(affinityId).then(result => {
      affinityLookupPending.value = false
      lastLookedUpAffinityId.value = affinityId
      if (result.existsInDistributor || result.success === false) {
        affinityError.value = (result.messages && result.messages[0]) || 'Affinity Id already exists in distributor table'
        return
      }
      runCreate()
    }).catch(() => {
      affinityLookupPending.value = false
      affinityError.value = 'Unable to look up Affinity Id'
    })
  } else {
    runCreate()
  }
}
</script>

<style scoped>
.page-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.table-scroll-wrapper {
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
}

.table-fixed :deep(table) {
  table-layout: fixed;
  min-width: 1630px;
  width: 100%;
}

.table-fixed :deep(th),
.table-fixed :deep(td) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 12px;
  padding-right: 12px;
}

.resizable-th {
  position: relative;
}

.th-label {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.th-label.sortable {
  cursor: pointer;
}

.col-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 8px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  z-index: 2;
}

.col-resize-handle:hover {
  background-color: rgba(var(--v-border-color), 0.4);
}

.modal-mask {
  position: fixed;
  z-index: 500;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 72px 16px 16px;
  box-sizing: border-box;
  overflow-y: auto;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
}

.modal-container {
  width: 100%;
  max-height: calc(100vh - 100px);
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  align-items: center;
  padding: 16px 24px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}

.modal-title {
  flex: 1;
}

.modal-actions {
  display: flex;
  align-items: center;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 30px 20px;
}

.modal-save-button {
  margin: 0px 10px;
}

.modal-cancel-button {
  margin: 0px 10px;
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>
